import { FetchMoreOptions, QueryHookOptions } from "@apollo/client";
import {
  GetAdditionalCostsQuery,
  GetAdditionalCostsQueryVariables,
  useGetAdditionalCostsQuery,
} from "generated/graphql";
import { useCallback, useMemo } from "react";

export const cacheKey = "GetAdditionalCosts";

export default function useGetAdditionalCosts(
  { page = 1, first = 10, search, sort }: GetAdditionalCostsQueryVariables,
  baseOptions: QueryHookOptions<
    GetAdditionalCostsQuery,
    GetAdditionalCostsQueryVariables
  > = undefined
) {
  const {
    data: graphqlData,
    loading,
    error,
    fetchMore: more,
  } = useGetAdditionalCostsQuery({
    variables: {
      page,
      first,
      search,
      sort,
    },
    ...baseOptions,
  });
  const fetchMore = useCallback(
    async ({
      page,
      updateQuery,
    }: {
      page: number;
      updateQuery?: FetchMoreOptions<
        GetAdditionalCostsQuery,
        GetAdditionalCostsQueryVariables
      >["updateQuery"];
    }) => {
      return more({
        variables: {
          page,
        },
        updateQuery,
      });
    },
    [more]
  );

  const getData = useCallback(({ data }: { data?: GetAdditionalCostsQuery }) => {
    return data?.getAdditionalCosts?.data || null;
  }, []);

  const getPaginatorInfo = useCallback(({ data }: { data?: GetAdditionalCostsQuery }) => {
    return data?.getAdditionalCosts?.paginatorInfo || null;
  }, []);

  const data = useMemo(() => {
    return getData({ data: graphqlData });
  }, [graphqlData, getData]);

  const paginatorInfo = useMemo(() => {
    return getPaginatorInfo({ data: graphqlData });
  }, [graphqlData]);

  return { data, loading, error, paginatorInfo, fetchMore, getData, getPaginatorInfo } as const;
}
